import React, { useEffect, useState, useRef } from 'react';
import { db } from '../config/firebase';
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { useParams } from 'react-router-dom';

export default function PropertyPage() {
  const { listingId } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  useEffect(() => {
    const fetchAndTrack = async () => {
      const docRef = doc(db, 'listings', listingId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setListing(data);
        document.title = `Listlytics | ${data.address}`;
      }
      setLoading(false);

      const now = new Date();
      await addDoc(collection(db, 'scans'), {
        listingId,
        timestamp: serverTimestamp(),
        hour: now.getHours(),
        dayOfWeek: now.toLocaleDateString('en-US', { weekday: 'short' }),
        date: now.toLocaleDateString()
      });
    };
    fetchAndTrack();
  }, [listingId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    await addDoc(collection(db, 'leads'), {
      listingId,
      ...form,
      submittedAt: serverTimestamp()
    });
    setSubmitted(true);
  };

  const nextPhoto = (photos) => setCurrentPhoto(p => (p + 1) % photos.length);
  const prevPhoto = (photos) => setCurrentPhoto(p => (p - 1 + photos.length) % photos.length);

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = (photos) => {
    if (!touchStartX.current || !touchEndX.current) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      diff > 0 ? nextPhoto(photos) : prevPhoto(photos);
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  if (loading) return <div style={styles.center}>Loading...</div>;
  if (!listing) return <div style={styles.center}>Property not found.</div>;

  const photos = listing.photos || [];

  return (
    <div style={styles.container}>

      {/* Photo Carousel */}
      {photos.length > 0 && (
        <div style={styles.carousel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={() => handleTouchEnd(photos)}>
          <img src={photos[currentPhoto]} alt={`Property ${currentPhoto + 1}`}
            style={styles.carouselImage} />
          {photos.length > 1 && (
            <>
              <button style={{...styles.carouselBtn, left: 10}}
                onClick={() => prevPhoto(photos)}>‹</button>
              <button style={{...styles.carouselBtn, right: 10}}
                onClick={() => nextPhoto(photos)}>›</button>
              <div style={styles.photoDots}>
                {photos.map((_, i) => (
                  <div key={i} style={{
                    ...styles.dot,
                    background: i === currentPhoto ? '#fff' : 'rgba(255,255,255,0.5)'
                  }} onClick={() => setCurrentPhoto(i)} />
                ))}
              </div>
              <div style={styles.photoCount}>{currentPhoto + 1} / {photos.length}</div>
            </>
          )}
        </div>
      )}

      {/* Property Details */}
      <div style={styles.details}>
        <h1 style={styles.price}>${Number(listing.price).toLocaleString()}</h1>
        <p style={styles.address}>{listing.address}</p>
        <div style={styles.badges}>
          <span style={styles.badge}>🛏 {listing.beds} Beds</span>
          <span style={styles.badge}>🚿 {listing.baths} Baths</span>
        </div>
        <p style={styles.description}>{listing.description}</p>
      </div>

      {/* Agent Card */}
      <div style={styles.agentCard}>
        <p style={styles.agentTitle}>LISTED BY</p>
        {listing.agentName && <p style={styles.agentName}>{listing.agentName}</p>}
        {listing.agentBrokerage && <p style={styles.agentBrokerage}>{listing.agentBrokerage}</p>}
        {listing.agentPhone && (
          <a href={`tel:${listing.agentPhone}`} style={styles.agentPhone}>
            📞 {listing.agentPhone}
          </a>
        )}
        {listing.agentEmail && (
          <a href={`mailto:${listing.agentEmail}`} style={styles.agentEmail}>
            ✉️ {listing.agentEmail}
          </a>
        )}
      </div>

      {/* Lead Form */}
      <div style={styles.form}>
        <h2 style={styles.formTitle}>📬 Request More Info</h2>
        {submitted ? (
          <p style={styles.success}>✅ Thanks! The agent will be in touch soon.</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <input style={styles.input} placeholder="Your Name *"
              value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
            <input style={styles.input} placeholder="Email *" type="email"
              value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
            <input style={styles.input} placeholder="Phone"
              value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
            <textarea style={styles.textarea} placeholder="Message (optional)"
              value={form.message} onChange={e => setForm({...form, message: e.target.value})} />
            <button type="submit" style={styles.button}>Send Request</button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { maxWidth: 480, margin: '0 auto', fontFamily: 'sans-serif', paddingBottom: 40 },
  center: { textAlign: 'center', marginTop: 100, fontSize: 18 },
  carousel: { position: 'relative', width: '100%', height: 280, background: '#000', userSelect: 'none' },
  carouselImage: { width: '100%', height: 280, objectFit: 'cover', pointerEvents: 'none' },
  carouselBtn: { position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(0,0,0,0.4)', color: '#fff', border: 'none', borderRadius: '50%',
    width: 36, height: 36, fontSize: 24, cursor: 'pointer', zIndex: 2 },
  photoDots: { position: 'absolute', bottom: 12, left: '50%',
    transform: 'translateX(-50%)', display: 'flex', gap: 6, zIndex: 2 },
  dot: { width: 8, height: 8, borderRadius: '50%', cursor: 'pointer' },
  photoCount: { position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.5)',
    color: '#fff', padding: '4px 8px', borderRadius: 12, fontSize: 12, zIndex: 2 },
  details: { padding: '20px 20px 0' },
  price: { fontSize: 28, fontWeight: 'bold', color: '#2E86AB', margin: '0 0 4px' },
  address: { fontSize: 16, color: '#555', margin: '0 0 12px' },
  badges: { display: 'flex', gap: 10, marginBottom: 12 },
  badge: { background: '#f0f0f0', padding: '6px 14px', borderRadius: 20, fontSize: 14 },
  description: { fontSize: 15, color: '#333', lineHeight: 1.6 },
  agentCard: { margin: 20, padding: 16, background: '#f9f9f9',
    borderRadius: 12, borderLeft: '4px solid #2E86AB' },
  agentTitle: { fontSize: 11, color: '#999', margin: '0 0 8px', letterSpacing: 1 },
  agentName: { fontSize: 18, fontWeight: 'bold', margin: '0 0 2px', color: '#333' },
  agentBrokerage: { fontSize: 14, color: '#666', margin: '0 0 8px' },
  agentPhone: { display: 'block', fontSize: 14, color: '#2E86AB', margin: '0 0 4px',
    textDecoration: 'none' },
  agentEmail: { display: 'block', fontSize: 14, color: '#2E86AB', margin: 0,
    textDecoration: 'none' },
  form: { padding: '0 20px' },
  formTitle: { fontSize: 18, marginBottom: 12 },
  input: { width: '100%', padding: 12, marginBottom: 10, fontSize: 15,
    border: '1px solid #ddd', borderRadius: 8, boxSizing: 'border-box' },
  textarea: { width: '100%', padding: 12, marginBottom: 10, fontSize: 15,
    border: '1px solid #ddd', borderRadius: 8, height: 80, boxSizing: 'border-box' },
  button: { width: '100%', padding: 16, background: '#2E86AB', color: '#fff',
    border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 'bold', cursor: 'pointer' },
  success: { color: 'green', fontSize: 16, textAlign: 'center' }
};




